import React, { useState, useEffect } from 'react';
import { WifiIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/**
 * Offline Connectivity Indicator
 * Shows a banner when the user loses internet connection
 * Auto-dismisses when connection is restored
 */
export default function OfflineIndicator() {
    const [isOffline, setIsOffline] = useState(!navigator.onLine);
    const [showReconnected, setShowReconnected] = useState(false);

    useEffect(() => {
        const handleOnline = () => {
            setIsOffline(false);
            setShowReconnected(true);
            // Hide "reconnected" message after 3 seconds
            setTimeout(() => setShowReconnected(false), 3000);
        };

        const handleOffline = () => {
            setIsOffline(true);
            setShowReconnected(false);
        };

        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);

        return () => {
            window.removeEventListener('online', handleOnline);
            window.removeEventListener('offline', handleOffline);
        };
    }, []);

    // Don't render anything if online and not showing reconnected message
    if (!isOffline && !showReconnected) return null;

    return (
        <div
            className={`fixed top-0 left-0 right-0 z-[9999] px-4 py-2 flex items-center justify-center space-x-2 text-sm font-bold transition-all duration-300 ${
                isOffline
                    ? 'bg-red-500 text-white'
                    : 'bg-green-500 text-white'
            }`}
            style={{ paddingTop: 'calc(0.5rem + env(safe-area-inset-top))' }}
        >
            {isOffline ? (
                <>
                    <ExclamationTriangleIcon className="w-5 h-5" />
                    <span>No internet connection</span>
                </>
            ) : (
                <>
                    <WifiIcon className="w-5 h-5" />
                    <span>Back online</span>
                </>
            )}
        </div>
    );
}
